import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Checkbox, IconButton } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useGridApiContext } from '@mui/x-data-grid-pro';

import SelectionText from './SelectionText';
import { SelectionType } from './SelectorTypes';

import './MultiElementSelectorHeader.css';
import { MoreVert } from '@mui/icons-material';
import MorePopup from './MorePopup';

export default function SelectionAndOrderController({
  selectedRowIds,
  resetSelection,
  selectionState,
  maxRowNum,
  selectMaxRows,
  totalRows,
  orderController,
  showMoreOptions,
}: {
  selectedRowIds?: GridRowSelectionModel;
  resetSelection?: () => void;
  selectionState: SelectionType;
  maxRowNum?: number;
  selectMaxRows?: () => void;
  totalRows: number;
  orderController: ReactElement;
  showMoreOptions?: boolean;
}) {
  const apiRef = useGridApiContext();
  const isAllrowsSelected = useCallback(
    (totalRows: number, selectedRows: number) => {
      if (selectionState === 'selected') return selectedRows > 0;
      return selectedRows === totalRows && totalRows > 0;
    },
    [selectionState]
  );
  const [allSelected, setAllSelected] = useState<boolean>(
    isAllrowsSelected(totalRows, selectedRowIds ? selectedRowIds.ids.size : 0)
  );
  const [gridReady, setGridReady] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isIndeterminate = useCallback(
    (totalRows: number, selectedRows: number) => {
      if (selectionState === 'selected') return false;
      return selectedRows > 0 && selectedRows < totalRows;
    },
    [selectionState]
  );
  const [indeterminate, setIndeterminate] = useState<boolean>(
    isIndeterminate(totalRows, selectedRowIds ? selectedRowIds.ids.size : 0)
  );

  useEffect(() => {
    if (apiRef.current) {
      setGridReady(true);
    }
  }, []);

  useEffect(() => {
    if (!apiRef.current) return;

    const updateSelectionState = () => {
      const totalRows = apiRef.current.getRowsCount();
      const selectedRows = selectedRowIds ? selectedRowIds.ids.size : 0;

      setAllSelected(isAllrowsSelected(totalRows, selectedRows));
      setIndeterminate(isIndeterminate(totalRows, selectedRows));
    };

    return apiRef.current.subscribeEvent('rowSelectionChange', updateSelectionState);
  }, [apiRef, selectedRowIds, selectionState, isAllrowsSelected, isIndeterminate]);

  const toggleSelectAll = () => {
    if (!resetSelection || !selectMaxRows) return;
    if (allSelected || indeterminate) {
      resetSelection();
    } else {
      selectMaxRows();
    }
  };

  function getDisabledClass() {
    if (!selectedRowIds || !maxRowNum) return '';
    if (
      (selectionState === 'selected' && selectedRowIds.ids.size === 0) ||
      apiRef.current.getRowsCount() === 0 ||
      !gridReady
    ) {
      return 'disabled';
    }
    return '';
  }

  return (
    <div className="selector-toolbar" data-qa-element-id="target_selector_toolbar">
      <span className={`${getDisabledClass()}`}>
        {selectionState !== 'single-select' && (
          <Checkbox
            data-qa-element-id="select_all_none_checkbox"
            className="selector-checkbox"
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={toggleSelectAll}
          />
        )}
        <SelectionText
          maxElementcount={maxRowNum}
          rowCount={totalRows}
          selectedCount={selectedRowIds ? selectedRowIds.ids.size : undefined}
          selectionState={selectionState}
        />
      </span>
      <span>
        {orderController}
        {showMoreOptions !== undefined && (
          <>
            <IconButton onClick={handleClick}>
              <MoreVert />
            </IconButton>
            <MorePopup anchorEl={anchorEl} handleClose={handleClose} />
          </>
        )}
      </span>
    </div>
  );
}
